import {useNotificationStore} from "@AppBuilderLib/features/notifications";
import AppBuilderImage from "@AppBuilderShared/components/shapediver/appbuilder/AppBuilderImage";
import {useExport} from "@AppBuilderShared/hooks/shapediver/parameters/useExport";
import {useShapeDiverStoreParameters} from "@AppBuilderShared/store/useShapeDiverStoreParameters";
import {EXPORT_TYPE} from "@shapediver/viewer.session";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useShallow} from "zustand/react/shallow";

interface Props {
	/**
	 * Default session namespace to use for parameter and export references that do
	 * not specify a session namespace.
	 */
	namespace: string;
	/** Id or name or displayname of the export to get the image from. */
	exportId: string;
}

export default function AppBuilderImageExportWidgetComponent(props: Props) {
	const {namespace, exportId, ...rest} = props;
	const {definition, actions} = useExport({namespace, exportId}) ?? {};
	const notifications = useNotificationStore();

	if (!definition || !actions) {
		notifications.error({
			message: `Export ${exportId} not found`,
		});
		return <></>;
	}

	const promiseChain = useRef(Promise.resolve());
	const objectUrl = useRef<string | undefined>(undefined);
	const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);
	const [contentType, setContentType] = useState<string | undefined>(
		undefined,
	);

	/**
	 * Create an object URL for the given href and set it as the image source.
	 */
	const setImageSrcCb = useCallback(
		(href: string) => {
			promiseChain.current = promiseChain.current.then(async () => {
				if (objectUrl.current) {
					URL.revokeObjectURL(objectUrl.current);
					objectUrl.current = undefined;
				}
				if (href) {
					const res = await actions.fetch(href);
					const blob = await res.blob();
					objectUrl.current = URL.createObjectURL(blob);
				}
				setImageSrc(objectUrl.current);
			});
		},
		[setImageSrc],
	);

	// Register the export to be requested on every parameter change.
	const {registerDefaultExport, deregisterDefaultExport} =
		useShapeDiverStoreParameters(
			useShallow((state) => ({
				registerDefaultExport: state.registerDefaultExport,
				deregisterDefaultExport: state.deregisterDefaultExport,
			})),
		);
	useEffect(() => {
		registerDefaultExport(namespace, definition.id);

		return () => deregisterDefaultExport(namespace, definition.id);
	}, [namespace, definition]);

	// Get responses to exports which were requested by default.
	const responses = useShapeDiverStoreParameters(
		(state) => state.defaultExportResponses[namespace],
	);

	useEffect(() => {
		if (responses && responses[definition.id]) {
			const response = responses[definition.id];
			if (
				response.content &&
				response.content[0] &&
				response.content[0].href
			) {
				const contentType = response.content[0].contentType;
				const href = response.content[0].href;
				setImageSrcCb(href);
				setContentType(contentType);
			} else {
				setImageSrc(undefined);
				setContentType(undefined);
			}
		} else {
			promiseChain.current = promiseChain.current.then(async () => {
				if (definition.type !== EXPORT_TYPE.DOWNLOAD) return;

				// request the export
				const response = await actions.request();

				if (
					response.content &&
					response.content[0] &&
					response.content[0].href
				) {
					const href = response.content[0].href;
					const contentType = response.content[0].contentType;

					setImageSrcCb(href);
					setContentType(contentType);
				} else {
					setImageSrc(undefined);
					setContentType(undefined);
				}
			});
		}
	}, [responses, definition]);

	if (imageSrc)
		return (
			<AppBuilderImage
				src={imageSrc}
				isSvg={contentType === "image/svg+xml"}
				{...rest}
			/>
		);
	else return <></>;
}
